/**
 * DealQuickCreateDialog - Minimal dialog for quick deal creation
 *
 * Following the Quick Create Dialog pattern:
 * - 3-5 fields maximum (Name, Pipeline/Stage, Organization)
 * - Redirects to detail page after creation for full editing
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { DialogForm, MonetaryAmountInput } from '../../../shared/components/common';
import {
  CustomFieldsSection,
  activeCustomFieldDefinitions,
  useCustomFieldDefinitions,
  validateCustomFields,
  type CustomFieldErrors,
} from '../../../shared/components/customFields';
import {
  CompanyCreateField,
  useCompanyCreatePrecedence,
} from '../../../shared/components/multi-company';
import { useCreateDeal } from '../hooks/useDeals';
import { useCreateDealFromLead } from '../hooks/useLeads';
import { useApiError } from '../../../shared/hooks/useApiError';
import { getFieldErrors } from '../../../shared/utils/errors';
import { usePipelines } from '../hooks/usePipelines';
import { useLastUsedPipeline } from '../hooks/useLastUsedPipeline';
import { useDefaultCurrency } from '../../../shared/hooks/useDefaultCurrency';
import { OrganizationPicker, ContactPicker } from '../../master_data/contacts';
import { PipelinePicker } from './PipelinePicker';
import type { Deal } from '../types';
import { getContactDisplayName } from '../../master_data/contacts/utils/contactName';

interface DealQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill name (e.g., "Copy of ...") */
  defaultName?: string;
  /** Pre-fill amount in dollars (display unit, not cents) */
  defaultAmount?: number | null;
  /** Pre-fill currency code */
  defaultCurrency?: string;
  /** Pre-fill the customer organization from context */
  defaultCompanyId?: string;
  /** Pre-fill the primary contact when launched from a contact context */
  defaultContactId?: string;
  /** Pre-fill stage from context (e.g., from kanban column) */
  defaultStageId?: string;
  /**
   * Pre-select a pipeline from context — outranks the remembered / tenant
   * default. Used by the Leads surface to seed the pipeline a lead's
   * questionnaire answers routed it to (lead-attribution design §D).
   */
  defaultPipelineId?: string;
  /**
   * Create the deal FROM this contact, rather than as a bare deal. Routes the
   * submit through `POST /leads/{contact_id}/create-deal` — the ONLY path that
   * carries the contact's source/source_detail, its custom-field answers and its
   * tags onto the new deal, and the one that resolves the customer server-side
   * (which is why this mode hides the contact/organization pickers).
   *
   * **The route is named for leads; its behaviour is not lead-only, and passing
   * a plain contact here is correct.** Everything it does is derived from the
   * contact itself: the attribution carry reads the contact's own values and its
   * own relationship scope, and the lifecycle writes go through the SAME
   * `LeadLifecycleService.apply_deal_created` seam `POST /deals` already calls —
   * whose rung transition is gated on the contact sitting at `lead`/`other`/
   * unclassified, so a prospect or customer is left untouched. The one behaviour
   * a plain create does NOT share is the permission bar: conversion requires
   * EDITOR on the contact (Contract D), because it can move the contact's
   * lifecycle. The routed-pipeline fallback is a no-op for a non-lead.
   *
   * Which entry points pass it, and why the others cannot: an entry point that
   * OWNS a contact (ContactDetailPage, LeadsListPage) passes it, so attribution
   * carries; an entry point with no contact (DealsListPage, OrganizationDetail-
   * Page, GetStartedPage) has nothing to carry and takes the plain create.
   */
  fromContactId?: string;
  /** Callback when deal is created (before navigation) */
  onCreated?: (deal: Deal) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
}

/** How the backend names a custom-field rejection (`field="custom_fields.<key>"`). */
const CUSTOM_FIELD_ERROR_PREFIX = 'custom_fields.';

interface QuickCreateFormData {
  name: string;
  pipeline_id: string;
  stage_id: string;
  organization_id?: string;
  primary_contact_id?: string;
  amount?: number | null;
  currency?: string;
  company_id?: string | null;
}

export function DealQuickCreateDialog({
  open,
  onClose,
  defaultName,
  defaultAmount,
  defaultCurrency: defaultCurrencyProp,
  defaultCompanyId,
  defaultContactId,
  defaultStageId,
  defaultPipelineId,
  fromContactId,
  onCreated,
  skipNavigation = false,
}: DealQuickCreateDialogProps) {
  const { t } = useTranslation(['pipeline', 'common']);
  const { t: tcf } = useTranslation('custom_fields');
  const navigate = useNavigate();
  const createMutation = useCreateDeal();
  const convertLeadMutation = useCreateDealFromLead();
  const { handleMutationError } = useApiError({ showToast: false });
  const { data: pipelinesData } = usePipelines();

  const { lastPipelineId } = useLastUsedPipeline();
  const companyPrecedence = useCompanyCreatePrecedence();
  const { defaultCompanyId: defaultSellingCompanyId } = companyPrecedence;
  // The single frontend seam (ERP § P13 at creation) — it also honours the
  // viewer's own preferred_currency, which the inline tenant-settings read did not.
  const viewerDefaultCurrency = useDefaultCurrency();
  const defaultCurrency = defaultCurrencyProp || viewerDefaultCurrency;

  // Find default pipeline and stage. Context wins (a lead's routed pipeline is
  // a statement about THIS deal), then the pipeline used for the last created
  // deal, then the tenant default, then the first.
  const defaultPipeline = useMemo(() => {
    if (!pipelinesData) return undefined;
    const fromContext = defaultPipelineId
      ? pipelinesData.find((p) => p.id === defaultPipelineId)
      : undefined;
    const remembered = lastPipelineId
      ? pipelinesData.find((p) => p.id === lastPipelineId)
      : undefined;
    return (
      fromContext || remembered || pipelinesData.find((p) => p.is_default) || pipelinesData[0]
    );
  }, [pipelinesData, lastPipelineId, defaultPipelineId]);

  const selectedPipelineId = defaultStageId
    ? pipelinesData?.find((p) => p.stages?.some((s) => s.id === defaultStageId))?.id
    : defaultPipeline?.id;

  const defaultStage = useMemo(() => {
    if (defaultStageId) {
      for (const pipeline of pipelinesData || []) {
        const stage = pipeline.stages?.find((s) => s.id === defaultStageId);
        if (stage) return stage;
      }
    }
    return defaultPipeline?.stages?.[0];
  }, [pipelinesData, defaultPipeline, defaultStageId]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      name: defaultName || '',
      pipeline_id: selectedPipelineId || '',
      stage_id: defaultStage?.id || '',
      organization_id: defaultCompanyId || undefined,
      primary_contact_id: defaultContactId || undefined,
      amount: defaultAmount ?? null,
      currency: defaultCurrency,
      company_id: defaultSellingCompanyId || null,
    },
  });

  // B2C pipelines lead with the individual contact and collapse the organization.
  const [showOrganization, setShowOrganization] = useState(false);

  // Deal custom fields, REQUIRED ones only. Both create routes validate
  // `partial=False`, so a tenant's required `pipeline_deal` field is not something
  // the detail page can collect afterwards — without it the create is refused, and
  // this dialog is the only create surface some entry points have. The optional
  // ones stay off the dialog: that is what keeps the quick-create budget honest
  // (STYLE_GUIDE § Dialog / Density) while the mandatory ones are askable at all.
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<CustomFieldErrors>({});
  const { data: cfData } = useCustomFieldDefinitions('pipeline_deal');
  const requiredCfDefinitions = useMemo(
    () => activeCustomFieldDefinitions(cfData?.definitions).filter((d) => d.required),
    [cfData?.definitions]
  );

  // Track whether the user has typed their own name. Until they do, selecting a
  // customer (organization for B2B, contact for B2C) seeds a default like
  // "ACME deal" / "Fred Smith deal" so most deals never need a manual name.
  // A caller-supplied defaultName (e.g. "Copy of …") counts as user intent.
  const nameEditedRef = useRef(Boolean(defaultName));

  // Seed the form exactly once per open. `pipelinesData` loads async, so
  // `defaultPipeline` is undefined on the first render(s) after open — we must
  // wait for it before seeding pipeline/stage, but once seeded we must NOT
  // re-seed when that data lands, or we'd wipe a name the user typed in the
  // meantime (the "This field is required" race).
  const formInitializedRef = useRef(false);

  const applyDefaultName = useCallback(
    (customerName: string | null | undefined) => {
      const trimmed = customerName?.trim();
      if (!trimmed || nameEditedRef.current) return;
      // shouldValidate clears any stale "required" error now that the seeded
      // name is a valid value — selecting a customer must not leave a red error.
      setValue('name', t('deals.defaultNameFromCustomer', { name: trimmed }), {
        shouldValidate: true,
      });
    },
    [setValue, t],
  );

  const watchedPipelineId = watch('pipeline_id');
  const watchedStageId = watch('stage_id');

  // Get stages for selected pipeline
  const selectedPipeline = useMemo(() => {
    return pipelinesData?.find((p) => p.id === watchedPipelineId);
  }, [pipelinesData, watchedPipelineId]);

  // B2C pipelines make the individual contact the headline association and
  // collapse the organization; B2B keeps the organization first-class.
  const isB2C = selectedPipeline?.customer_segment === 'b2c';

  useEffect(() => {
    if (!open) {
      // Reset the one-shot guards on close so the next open re-seeds cleanly.
      formInitializedRef.current = false;
      nameEditedRef.current = Boolean(defaultName);
      return;
    }
    if (formInitializedRef.current || !defaultPipeline) return;
    formInitializedRef.current = true;
    // Preserve a name the user typed while pipelinesData was still loading —
    // re-seeding it to the default here is what caused the "required" race.
    const preservedName = nameEditedRef.current ? getValues('name') : defaultName || '';
    reset({
      name: preservedName,
      pipeline_id: selectedPipelineId || defaultPipeline.id,
      stage_id: defaultStage?.id || defaultPipeline.stages?.[0]?.id || '',
      organization_id: defaultCompanyId || undefined,
      primary_contact_id: defaultContactId || undefined,
      amount: defaultAmount ?? null,
      currency: defaultCurrency,
      company_id: defaultSellingCompanyId || null,
    });
    setShowOrganization(false);
    setCustomFields({});
    setCustomFieldErrors({});
  }, [open, defaultPipeline, defaultStage, selectedPipelineId, defaultCompanyId, defaultContactId, defaultSellingCompanyId, defaultName, defaultAmount, defaultCurrency, reset, getValues]);

  // Company data and pipeline data load independently. If the sole/scoped
  // company resolves after the one-shot form initialization, seed it without
  // resetting customer/name input the user may already have entered.
  useEffect(() => {
    if (open && defaultSellingCompanyId && !getValues('company_id')) {
      setValue('company_id', defaultSellingCompanyId, { shouldValidate: true });
    }
  }, [open, defaultSellingCompanyId, getValues, setValue]);

  // Reset stage when pipeline changes
  useEffect(() => {
    if (selectedPipeline && !defaultStageId) {
      const firstStage = selectedPipeline.stages?.[0];
      if (firstStage) {
        setValue('stage_id', firstStage.id);
      }
    }
  }, [watchedPipelineId, selectedPipeline, defaultStageId, setValue]);

  // Pipeline/stage are seeded asynchronously from `usePipelines`. Until they land
  // the form's required pipeline_id/stage_id are empty, so a submit can only fail
  // validation — and those two controls surface that as a bare red border, i.e. a
  // Create click that silently does nothing. Gate the primary action on them
  // instead: the button stays disabled while the pipelines query is in flight (and
  // permanently, if the tenant has no pipeline at all, which is the honest state).
  const canSubmit = Boolean(watchedPipelineId && watchedStageId);

  // `handleMutationError` files every field error the envelope names; this drops
  // the custom-field ones on the floor so they never reach react-hook-form (they
  // are rendered by `CustomFieldsSection` instead — see the catch block below).
  const setNonCustomFieldError: typeof setError = (name, error, options) => {
    if (String(name).startsWith(CUSTOM_FIELD_ERROR_PREFIX)) return;
    setError(name, error, options);
  };

  const onSubmit = async (data: QuickCreateFormData) => {
    // Precedence rule 2's acknowledgement. NOT in the `validate` above:
    // react-hook-form caches a field error until that field re-validates, and
    // confirming the chip changes no value, so the message would outlive the
    // condition. Here it is the field's own, and `confirm()` clears it.
    if (companyPrecedence.blockCreate(data.company_id ?? '')) return;
    const money =
      data.amount != null
        ? { amount: data.amount, currency_code: data.currency || defaultCurrency }
        : {};

    // Required custom fields are checked client-side ONLY on the plain create.
    // The contact-origin route resolves the contact's own carried answers first
    // (lead-attribution design §D), so a field the contact already answers is
    // satisfied server-side and blocking submit here would refuse a conversion
    // the server accepts — a value the user cannot see is not theirs to re-key.
    // That path's required check is the route's own `partial=False`, whose
    // `custom_fields.<key>` field error lands back on this same input below.
    if (!fromContactId) {
      const cfErrors = validateCustomFields(requiredCfDefinitions, customFields, tcf);
      setCustomFieldErrors(cfErrors);
      if (Object.keys(cfErrors).length > 0) return;
    } else {
      setCustomFieldErrors({});
    }
    const cfPayload =
      requiredCfDefinitions.length > 0 ? { custom_fields: customFields } : {};

    try {
      // A deal created FROM a contact goes through the contact-origin route —
      // same fields, but it also carries that contact's source, custom-field
      // answers and tags onto the deal. See `fromContactId` above for why this
      // is right for a plain contact and not only for a lead.
      const newDeal = fromContactId
        ? await convertLeadMutation.mutateAsync({
            contactId: fromContactId,
            data: {
              name: data.name,
              pipeline_id: data.pipeline_id,
              stage_id: data.stage_id,
              company_id: data.company_id || undefined,
              ...money,
              ...cfPayload,
            },
          })
        : await createMutation.mutateAsync({
            name: data.name,
            pipeline_id: data.pipeline_id,
            stage_id: data.stage_id,
            organization_id: data.organization_id || undefined,
            primary_contact_id: data.primary_contact_id || undefined,
            company_id: data.company_id || undefined,
            ...money,
            ...cfPayload,
          });
      onCreated?.(newDeal);
      onClose();

      if (!skipNavigation) {
        navigate(`/pipeline/deals/${newDeal.id}`);
      }
    } catch (error) {
      // A custom-field rejection names `custom_fields.<key>`, which is no field of
      // this form. Route those onto the inputs that own them — and keep them OUT
      // of react-hook-form: an error set on a name the form never registered is
      // never cleared by validating the fields it did, so `handleSubmit` refuses
      // every later submit. The user then fixes the field and Create does
      // nothing, for the rest of the dialog's life.
      const cfErrors: CustomFieldErrors = {};
      for (const [field, message] of Object.entries(getFieldErrors(error) ?? {})) {
        if (field.startsWith(CUSTOM_FIELD_ERROR_PREFIX)) {
          cfErrors[field.slice(CUSTOM_FIELD_ERROR_PREFIX.length)] = message;
        }
      }
      setCustomFieldErrors(cfErrors);
      handleMutationError(error, setNonCustomFieldError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('deals.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting}
      isDisabled={!canSubmit}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label required>{t('deals.pipeline')}</Label>
            <Controller
              name="pipeline_id"
              control={control}
              rules={{ required: t('common:validation.required') }}
              render={({ field }) => (
                <PipelinePicker
                  triggerId="deal-pipeline-picker"
                  value={field.value}
                  onChange={field.onChange}
                  onPipelineSelected={(pipeline) => {
                    // When a pipeline is created/selected inline, land the deal
                    // in its first stage so the Stage select isn't left empty.
                    const firstStage = pipeline?.stages?.[0];
                    if (firstStage) setValue('stage_id', firstStage.id);
                  }}
                  placeholder={t('common:app.select')}
                  error={!!errors.pipeline_id}
                />
              )}
            />
            {errors.pipeline_id && (
              <p className="text-sm text-destructive">{errors.pipeline_id.message}</p>
            )}
            {/* Say WHY this pipeline is pre-selected, and stop saying it the
                moment the user picks another — a silent pre-selection reads as
                the app's default rather than as this lead's routing. The gate is
                a contact origin AND a context pipeline, which only the Leads
                surface supplies — a lead's `routed_pipeline_id`. The kanban row
                button also pre-selects a pipeline, but there the user just
                clicked that row, so "this lead's routing" would be a lie; and
                the contact detail page passes no context pipeline at all, so a
                plain contact never sees this copy either. */}
            {fromContactId && defaultPipelineId && watchedPipelineId === defaultPipelineId && (
              <p className="text-xs text-muted-foreground">
                {t('deals.pipelineFromLeadRouting')}
              </p>
            )}
          </div>

          {/* Stage stays a plain select scoped to the chosen pipeline — documented
              exception to the inline-create policy (a stage's position/type/
              probability/gate-criteria are funnel design owned by the Pipelines
              page; a bare inline stage mid-deal would be malformed). */}
          <div className="space-y-2">
            <Label required>{t('deals.stage')}</Label>
            <Controller
              name="stage_id"
              control={control}
              rules={{ required: t('common:validation.required') }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    className={cn(errors.stage_id && 'border-destructive')}
                    data-testid="deal-stage-select"
                  >
                    <SelectValue placeholder={t('common:app.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedPipeline?.stages?.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.stage_id && (
              <p className="text-sm text-destructive">{errors.stage_id.message}</p>
            )}
          </div>
        </div>

        {/* Creating from a contact fixes the customer: the deal's contact and
            organization are that contact's, resolved server-side. Rendering an
            editable picker here would offer a choice the route ignores. */}
        {fromContactId ? null : isB2C ? (
          <>
            {/* B2C: the individual contact is the headline association — selecting
                it seeds the default deal name. */}
            <div className="space-y-2">
              <Label>{t('deals.contact')}</Label>
              <Controller
                name="primary_contact_id"
                control={control}
                render={({ field }) => (
                  <ContactPicker
                    value={field.value || ''}
                    onChange={(id) => field.onChange(id || undefined)}
                    onContactSelected={(contact) =>
                      applyDefaultName(
                        contact ? getContactDisplayName(contact) : null,
                      )
                    }
                    allowClear
                    placeholder={t('deals.contactPlaceholder')}
                  />
                )}
              />
            </div>
            {/* Organization is optional for a consumer deal — collapsed by default. */}
            {showOrganization ? (
              <div className="space-y-2">
                <Label>{t('deals.company')}</Label>
                <Controller
                  name="organization_id"
                  control={control}
                  render={({ field }) => (
                    <OrganizationPicker
                      value={field.value || ''}
                      onChange={(id) => field.onChange(id || undefined)}
                      allowClear
                      defaultRelationships={['lead', 'prospect']}
                    />
                  )}
                />
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="inline-flex items-center text-xs text-primary hover:underline cursor-pointer h-auto p-0"
                onClick={() => setShowOrganization(true)}
              >
                <Plus className="h-3.5 w-3.5 me-1" />
                {t('deals.addOrganization')}
              </Button>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <Label>{t('deals.company')}</Label>
            <Controller
              name="organization_id"
              control={control}
              render={({ field }) => (
                <OrganizationPicker
                  triggerId="deal-organization-picker"
                  value={field.value || ''}
                  onChange={(id) => field.onChange(id || undefined)}
                  onCompanySelected={(company) => applyDefaultName(company?.name)}
                  allowClear
                  defaultRelationships={['lead', 'prospect']}
                />
              )}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="name" required>{t('deals.name')}</Label>
          <Controller
            name="name"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Input
                {...field}
                id="name"
                onChange={(e) => {
                  nameEditedRef.current = true;
                  field.onChange(e);
                }}
                placeholder={t('deals.namePlaceholder')}
                className={cn(errors.name && 'border-destructive')}
              />
            )}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <MonetaryAmountInput
          currency={watch('currency') || defaultCurrency}
          amount={watch('amount')}
          onCurrencyChange={(cur) => setValue('currency', cur || defaultCurrency)}
          onAmountChange={(amt) => setValue('amount', amt)}
          amountLabel={t('deals.amount')}
          size="sm"
        />

        <Controller
          name="company_id"
          control={control}
          rules={{ required: t('common:validation.required') }}
          render={({ field }) => (
            <CompanyCreateField
              precedence={companyPrecedence}
              id="deal-selling-company"
              value={field.value ?? ''}
              onChange={(companyId) => field.onChange(companyId || null)}
              label={t('deals.dealCompany', 'Selling {{Company}}') as string}
              error={!!errors.company_id}
              errorMessage={errors.company_id?.message}
            />
          )}
        />

        {/* Only the tenant's REQUIRED deal custom fields — the create endpoint
            refuses without them, so they are part of the minimum this dialog owes,
            not the optional detail the detail page collects. */}
        {requiredCfDefinitions.length > 0 && (
          <CustomFieldsSection
            entityType="pipeline_deal"
            requiredOnly
            values={customFields}
            onChange={setCustomFields}
            errors={customFieldErrors}
            currency={watch('currency') || defaultCurrency}
          />
        )}

        <p className="text-xs text-muted-foreground">
          {t('deals.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
